import { inject, Injector } from '@angular/core';
import type { ActivatedRouteSnapshot, ResolveFn } from '@angular/router';
import { RedirectCommand, Router } from '@angular/router';
import type { OnyxHttpErrorResponse } from '@onyx/angular';
import { OnyxToastService } from '@onyx/angular';
import { catchError, map, of } from 'rxjs';
import { VALIDATION_ERROR_CODES } from '../../../../common/constants/common/validation-error-codes';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { DriverFormResolverData } from '../../drivers.routes';
import { DriversRoute } from '../enums/drivers-route';
import { DriverFormHelper } from '../helpers/driver-form.helper';
import { DriverHelper } from '../helpers/driver.helper';
import { DriversService } from '../services/drivers.service';

export const editDriverResolver: ResolveFn<DriverFormResolverData> = (
  route: ActivatedRouteSnapshot,
) => {
  const driverService = inject(DriversService);
  const toastService = inject(OnyxToastService);
  const injector = inject(Injector);
  const router = inject(Router);

  const uuid = route.paramMap.get('uuid')!;
  const urlTree = router.parseUrl(DriversRoute.DRIVERS_LIST);

  return driverService.getDriver(uuid).pipe(
    map((dto) => {
      if (DriverHelper.isArchived(dto)) {
        toastService.showError({
          message: 'toasts.archived',
          keepOnNavigation: true,
        });
        return new RedirectCommand(urlTree, { replaceUrl: true });
      }

      const form = DriverFormHelper.fromDto(dto);
      return { initial: form, form };
    }),
    catchError((response: OnyxHttpErrorResponse) => {
      if (VALIDATION_ERROR_CODES.includes(response.status)) {
        toastService.showError({
          message: 'drivers.driverForm.driverNotFound',
          keepOnNavigation: true,
        });
        response.handled = true;
      } else {
        ValidationHelper.handleUnexpectedResponse(response, injector, {
          keepOnNavigation: true,
        });
      }

      return of(new RedirectCommand(urlTree, { replaceUrl: true }));
    }),
  );
};
